"use client";

import React from "react";
import { getCurrentUserContext, type UserContextResponse } from "@/lib/api/client/users";
import { getAuthToken, installAuthFetchInterceptor, setAuthToken } from "@/lib/auth/client-token";
import { useUserStore } from "@/lib/stores/user-store";

type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "error";

type ClientAuthContextValue = {
  status: AuthStatus;
  data: UserContextResponse | null;
  error: string | null;
  refresh: () => Promise<void>;
};

const ClientAuthContext = React.createContext<ClientAuthContextValue | undefined>(undefined);

export function ClientAuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<AuthStatus>("loading");
  const [data, setData] = React.useState<UserContextResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const token = useUserStore((state) => state.token);
  const hydrated = useUserStore((state) => state.hydrated);

  React.useEffect(() => {
    installAuthFetchInterceptor();
    if (typeof window === "undefined") {
      return;
    }

    // One-time migration from legacy raw localStorage token key.
    const hasStoreToken = Boolean(getAuthToken());
    if (!hasStoreToken) {
      const legacyToken = window.localStorage.getItem("auth_token");
      if (legacyToken && legacyToken.trim()) {
        setAuthToken(legacyToken);
      }
    }

  }, []);

  const load = React.useCallback(async () => {
    if (!hydrated) {
      setStatus("loading");
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      setData(null);
      setError(null);
      setStatus("unauthenticated");
      return;
    }

    try {
      setStatus("loading");
      setError(null);
      const nextData = await getCurrentUserContext();
      setData(nextData);
      setStatus("authenticated");
    } catch (cause: any) {
      if (cause?.status === 401) {
        setData(null);
        setStatus("unauthenticated");
        return;
      }
      setData(null);
      setStatus("error");
      setError(cause?.message ?? "Failed to load auth state");
    }
  }, [hydrated, token]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const value = React.useMemo(
    () => ({
      status,
      data,
      error,
      refresh: load,
    }),
    [data, error, load, status],
  );

  return <ClientAuthContext.Provider value={value}>{children}</ClientAuthContext.Provider>;
}

function useClientAuthContext() {
  const context = React.useContext(ClientAuthContext);
  if (!context) {
    throw new Error("useClientAuth must be used within ClientAuthProvider");
  }
  return context;
}

export function useClientAuth() {
  return useClientAuthContext();
}

export function useCurrentUser() {
  const { status, data } = useClientAuthContext();
  return status === "authenticated" ? data?.user ?? null : null;
}

export function useActiveOrganization() {
  const { status, data } = useClientAuthContext();
  return status === "authenticated" ? data?.activeOrganization ?? null : null;
}
